import { useMemo, type CSSProperties, type ReactNode } from 'react';
import { Box, Chip, Stack, Typography } from '@mui/material';
import { walidujMotyw } from '@/lib/tresci/sezon/walidator';
import type { MotywSezonowy } from '@/lib/tresci/sezon/kontrakt';
import type { Dlugosc, Region, ScenaRegionu, Tlo, Warstwa } from '@/lib/tresci/sezon/scena.model';

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * PODGLĄD SKÓRKI NA TELEFONIE — z PRAWDZIWEJ paczki (OWNER 11.10)
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * ┌─ CO JEST WIERNE, A CO PRZYBLIŻONE ─────────────────────────────────────────────────────────────┐
 * │ Wejściem jest `motyw.scena` z KOPII silnika aplikacji (walidator R5-10, `sezon/`) — ten sam      │
 * │ graf sceny, który rysuje telefon (schemat 3 wprost, silnik 1/2 przez adapter). Pozycje, rozmiary,│
 * │ kotwice, obrót, lustro, krycie, kolejność warstw, tła regionów, paleta i pasek okazji pochodzą  │
 * │ z paczki. Makieta ekranu menu (kafle, teksty przykładowe) i ruch są PRZYBLIŻONE: efekty mają    │
 * │ prostą animację CSS, cząstki są rozsiane deterministycznie. Paczka odrzucona przez silnik =     │
 * │ zwykły wygląd — dokładnie jak w aplikacji.                                                      │
 * └──────────────────────────────────────────────────────────────────────────────────────────────────┘
 */

const SZER = 320;
const WYS_EKRANU = 640;
const NAGLOWEK = 118;
const PASEK = 34;
const NAWIGACJA = 54;

/** Adres zasobu → to, co da się wczytać w panelu (HTTPS z paczki albo plik lokalny przed wgraniem). */
export type RozwiazAdres = (url: string) => string | null;

const domyslneRozwiazanie: RozwiazAdres = (url) => (/^https:\/\//i.test(url) ? url : null);

function dl(d: Dlugosc | null, baza: number): number | undefined {
  if (!d) return undefined;
  switch (d.u) {
    case 'px': return d.v;
    case '%': return (d.v / 100) * baza;
    case 'vw': return (d.v / 100) * SZER;
    case 'vh': return (d.v / 100) * WYS_EKRANU;
  }
}

function tloCss(t: Tlo | null, rozwiaz: RozwiazAdres): CSSProperties {
  if (!t) return {};
  const krycie = { opacity: t.krycie };
  if (t.typ === 'SOLID' && t.kolor) return { background: t.kolor, ...krycie };
  if (t.typ === 'GRADIENT' && t.przystanki.length) {
    return { background: `linear-gradient(${t.kat}deg, ${t.przystanki.map((p) => `${p.kolor} ${p.at * 100}%`).join(', ')})`, ...krycie };
  }
  if (t.typ === 'IMAGE' && t.url) {
    const adres = rozwiaz(t.url);
    if (!adres) return {};
    const rozmiar = t.dopasowanie === 'COVER' ? 'cover' : t.dopasowanie === 'CONTAIN' ? 'contain' : t.dopasowanie === 'STRETCH' ? '100% 100%' : 'auto';
    return {
      backgroundImage: `url("${adres}")`, backgroundSize: rozmiar, backgroundRepeat: t.dopasowanie === 'REPEAT' ? 'repeat' : 'no-repeat',
      backgroundPosition: `${t.pozycja[0] * 100}% ${t.pozycja[1] * 100}%`, ...krycie,
    };
  }
  return {};
}

/** Prosta animacja CSS dla efektu warstwy — przybliżenie ruchu z aplikacji. */
function animacja(w: Warstwa): string | undefined {
  const e = w.efekty[0];
  if (!e) return undefined;
  const czas = Math.max(600, e.czas + e.przerwa);
  const mapa: Record<string, string> = {
    FLOAT: 'sk-unos', BOB: 'sk-unos', BOUNCE: 'sk-unos', DRIFT: 'sk-dryf', FLY: 'sk-dryf', SHAKE_LIGHT: 'sk-dryf',
    SWAY: 'sk-kolys', WIGGLE: 'sk-kolys', SPIN: 'sk-obrot', ROTATE: 'sk-obrot', ORBIT: 'sk-obrot',
    FALL: 'sk-opad', RISE: 'sk-wznos', PULSE: 'sk-puls', ZOOM: 'sk-puls',
    FADE: 'sk-mig', BLINK: 'sk-mig', SHIMMER: 'sk-mig', GLOW: 'sk-mig', SPARKLE: 'sk-mig',
  };
  const nazwa = mapa[e.typ];
  return nazwa ? `${nazwa} ${czas}ms ${e.easing === 'linear' ? 'linear' : 'ease-in-out'} ${e.opoznienie}ms infinite` : undefined;
}

/** Pseudolosowość deterministyczna — ten sam podgląd przy każdym renderze. */
function los(ziarno: number): number {
  const x = Math.sin(ziarno * 9301 + 49297) * 233280;
  return x - Math.floor(x);
}

function WarstwaWidok({ w, szer, wys, rozwiaz }: { w: Warstwa; szer: number; wys: number; rozwiaz: RozwiazAdres }): ReactNode {
  const transformacje = [
    `translate(${-w.poczatek[0] * 100}%, ${-w.poczatek[1] * 100}%)`,
    w.obrot ? `rotate(${w.obrot}deg)` : '',
    w.skala !== 1 ? `scale(${w.skala})` : '',
    w.lustro === 'X' ? 'scaleX(-1)' : w.lustro === 'Y' ? 'scaleY(-1)' : '',
  ].filter(Boolean).join(' ');
  const pozycja: CSSProperties = w.tryb === 'FLOW'
    ? { position: 'relative', display: 'block', width: dl(w.szer, szer) ?? '100%', height: dl(w.wys, wys), opacity: w.krycie, zIndex: w.z }
    : {
      position: 'absolute', left: (dl(w.x, szer) ?? 0) + w.dx, top: (dl(w.y, wys) ?? 0) + w.dy,
      width: dl(w.szer, szer), height: dl(w.wys, wys), maxWidth: w.maxSzer ?? undefined, maxHeight: w.maxWys ?? undefined,
      transform: transformacje, transformOrigin: '0 0', opacity: w.krycie, zIndex: w.z, pointerEvents: 'none',
    };
  const ruch = animacja(w);

  if (w.typ === 'IMAGE') {
    const adres = rozwiaz(w.urlStatyczny ?? w.url) ?? rozwiaz(w.url);
    if (!adres) return <Box sx={{ ...pozycja, border: '1px dashed #c33', fontSize: 9, color: '#c33', p: 0.25 }}>brak pliku</Box>;
    const dop = w.dopasowanie === 'COVER' ? 'cover' : w.dopasowanie === 'FILL' ? 'fill' : w.dopasowanie === 'NONE' ? 'none' : 'contain';
    return (
      <Box sx={{ ...pozycja }}>
        <img src={adres} alt={`warstwa ${w.id}`} data-warstwa={w.id}
          style={{ width: '100%', height: w.wys ? '100%' : 'auto', objectFit: dop, display: 'block', animation: ruch }} />
      </Box>
    );
  }
  if (w.typ === 'TEXT') {
    return (
      <Box sx={{ ...pozycja, color: w.kolor, textAlign: w.wyrownanie, fontFamily: w.font.rodzina, fontWeight: w.font.waga,
        fontSize: w.font.rozmiar, lineHeight: w.font.interlinia, letterSpacing: w.font.odstep,
        // Bez pochylenia: reguła typografii CC (T8/T9) — tekst warstwy z `kursywa` rysuje się prosto (przybliżenie).
        textTransform: w.font.wersaliki ? 'uppercase' : 'none',
        textShadow: w.cien ? `0 0 ${w.cien.rozmycie}px ${w.cien.kolor}` : undefined, animation: ruch }}>
        {w.tekst}
      </Box>
    );
  }
  if (w.typ === 'SHAPE') {
    return (
      <Box sx={{ ...pozycja, ...tloCss(w.wypelnienie, rozwiaz), opacity: w.krycie,
        borderRadius: w.ksztalt === 'ELLIPSE' ? '50%' : `${w.zaokraglenie}px`,
        border: w.obrys ? `${w.obrys.grubosc}px solid ${w.obrys.kolor}` : undefined, animation: ruch }} />
    );
  }
  // PARTICLES — kilka cząstek rozsianych w obszarze narodzin (bez fizyki).
  const adres = rozwiaz(w.url);
  if (!adres) return null;
  const [ox, oy, ow, oh] = w.obszar;
  return (
    <>
      {Array.from({ length: Math.min(w.ile, 14) }, (_, i) => (
        <img key={i} src={adres} alt="" aria-hidden
          style={{ position: 'absolute', left: (ox + los(i + 1) * ow) * szer, top: (oy + los(i + 101) * oh) * wys,
            width: w.rozmiar, opacity: w.kryciePrzedzial[1], transform: `rotate(${Math.round(los(i + 7) * 360)}deg)`,
            animation: `sk-opad ${2500 + Math.round(los(i + 3) * 2000)}ms linear ${Math.round(los(i + 5) * 1500)}ms infinite`,
            pointerEvents: 'none', zIndex: w.z }} />
      ))}
    </>
  );
}

function RegionWidok({ r, szer, wys, rozwiaz, children, sx }: {
  r: ScenaRegionu | undefined; szer: number; wys: number; rozwiaz: RozwiazAdres; children?: ReactNode; sx?: object;
}) {
  const warstwy = [...(r?.warstwy ?? [])].sort((a, b) => a.z - b.z);
  return (
    // `isolation`: region = własny kontekst nakładania. Warstwy z ujemnym `z` (np. tło nagłówka pod logo, z = -1)
    // rysują się NAD tłem regionu i POD jego treścią — bez tego chowały się pod tłem i podgląd był pusty.
    <Box data-region={r?.region} sx={{ position: 'relative', isolation: 'isolate', width: szer, height: wys, overflow: r?.przytnij === false ? 'visible' : 'hidden', ...sx }}>
      {r?.tlo ? <Box sx={{ position: 'absolute', inset: 0, ...tloCss(r.tlo, rozwiaz) }} /> : null}
      {children}
      {warstwy.map((w) => <WarstwaWidok key={w.id} w={w} szer={szer} wys={wys} rozwiaz={rozwiaz} />)}
    </Box>
  );
}

/** Wysokość regionu w przepływie (EVENT_HERO, PRE_RESTAURANTS, POST_CONTENT): px, z proporcji albo 0 bez warstw. */
function wysokoscPrzeplywu(r: ScenaRegionu | undefined): number {
  if (!r || (r.warstwy.length === 0 && !r.tlo)) return 0;
  if (typeof r.wysokosc === 'number') return Math.min(r.wysokosc, r.maxWysokosc ?? r.wysokosc);
  if (r.proporcja) return Math.min(SZER / r.proporcja, r.maxWysokosc ?? 480);
  return 160;
}

export interface PodgladTelefonuProps {
  /** Surowa paczka (`motyw.json` albo `package` rewizji) — walidowana tu silnikiem aplikacji. */
  paczka: unknown;
  rozwiaz?: RozwiazAdres;
  /** Tekst paska z „Pasek główny" — ma pierwszeństwo przed paskiem okazji z motywu. */
  komunikatPaska?: string | null;
}

export function PodgladTelefonu({ paczka, rozwiaz = domyslneRozwiazanie, komunikatPaska = null }: PodgladTelefonuProps) {
  const motyw: MotywSezonowy | null = useMemo(() => walidujMotyw(paczka).motyw, [paczka]);
  const regiony = motyw?.scena.regiony ?? {};
  const reg = (n: Region) => regiony[n];
  const paleta = motyw?.paleta ?? null;
  const pasek = motyw?.pasek ?? null;
  const efekty = [...new Set(Object.values(regiony).flatMap((r) => (r?.warstwy ?? []).flatMap((w) => w.efekty.map((e) => e.typ))))];
  const wysHero = wysokoscPrzeplywu(reg('EVENT_HERO'));
  const wysPre = wysokoscPrzeplywu(reg('PRE_RESTAURANTS'));
  const wysPost = wysokoscPrzeplywu(reg('POST_CONTENT'));
  const tekstPaska = komunikatPaska ?? (pasek ? pasek.tekst : 'Dowóz dziś · zamów do 9:30');

  const kafel = (i: number) => (
    <Box key={i} sx={{ width: 132, flex: '0 0 auto', borderRadius: 2, overflow: 'hidden', bgcolor: paleta?.karta ?? '#fff',
      border: `1px solid ${paleta?.linia ?? '#e5e2da'}` }}>
      <Box sx={{ height: 70, bgcolor: '#d9d4c7' }} />
      <Box sx={{ p: 0.75 }}>
        <Typography sx={{ fontSize: 11, fontWeight: 700, color: paleta?.tekst ?? '#1d1b1a' }}>Danie dnia {i}</Typography>
        <Typography sx={{ fontSize: 10, color: paleta?.pomocniczy ?? '#6b665c' }}>Restauracja · 4,8 ★</Typography>
      </Box>
    </Box>
  );
  const sekcja = (tytul: string) => (
    <Box sx={{ px: 1.5, pt: 1.5 }}>
      <RegionWidok r={reg('SECTION_HEADER')} szer={SZER - 24} wys={24} rozwiaz={rozwiaz}>
        <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', height: 24, position: 'relative', zIndex: 1 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 800, color: paleta?.tekst ?? '#1d1b1a' }}>{tytul}</Typography>
          <Typography sx={{ fontSize: 10, color: paleta?.pomocniczy ?? '#6b665c' }}>Pokaż wszystkie</Typography>
        </Stack>
      </RegionWidok>
      <Stack direction="row" spacing={1} sx={{ mt: 1, overflow: 'hidden' }}>{[1, 2, 3].map(kafel)}</Stack>
    </Box>
  );

  return (
    <Stack spacing={1} sx={{ alignItems: 'center' }}>
      <style>{`
        @keyframes sk-unos { 0%,100% { translate: 0 0 } 50% { translate: 0 -6px } }
        @keyframes sk-dryf { 0%,100% { translate: 0 0 } 50% { translate: 8px 0 } }
        @keyframes sk-kolys { 0%,100% { rotate: -6deg } 50% { rotate: 6deg } }
        @keyframes sk-obrot { to { rotate: 360deg } }
        @keyframes sk-opad { 0% { translate: 0 -20px; opacity: 0 } 15% { opacity: 1 } 100% { translate: 6px 120px; opacity: 0 } }
        @keyframes sk-wznos { 0% { translate: 0 20px; opacity: 0 } 100% { translate: 0 -60px; opacity: 0 } 20% { opacity: 1 } }
        @keyframes sk-puls { 0%,100% { scale: 1 } 50% { scale: 1.08 } }
        @keyframes sk-mig { 0%,100% { opacity: 1 } 50% { opacity: .35 } }
        @media (prefers-reduced-motion: reduce) { [data-podglad-telefonu] * { animation: none !important } }
      `}</style>
      <Box data-podglad-telefonu aria-label="Podgląd skórki na telefonie" role="img"
        sx={{ width: SZER + 20, p: '10px', borderRadius: '34px', bgcolor: '#111', boxShadow: 3 }}>
        <Box sx={{ position: 'relative', width: SZER, height: WYS_EKRANU, borderRadius: '26px', overflow: 'hidden', bgcolor: paleta?.tlo ?? '#f6f4ef' }}>
          {/* NAGŁÓWEK: tło sceny pod logo, datą i zegarem */}
          <RegionWidok r={reg('APP_HEADER')} szer={SZER} wys={NAGLOWEK} rozwiaz={rozwiaz} sx={{ bgcolor: '#1d1b1a' }}>
            <Box sx={{ position: 'absolute', left: 16, top: 52, zIndex: 2 }}>
              <RegionWidok r={reg('LOGO')} szer={120} wys={40} rozwiaz={rozwiaz} sx={{ overflow: 'visible' }}>
                <Typography sx={{ color: '#FFD400', fontWeight: 900, fontSize: 24, position: 'relative', zIndex: 1 }}>Tu Szama!</Typography>
              </RegionWidok>
            </Box>
            <Typography sx={{ position: 'absolute', right: 16, top: 60, color: '#fff', fontSize: 11, zIndex: 2 }}>pon. 11.10 · 08:15</Typography>
          </RegionWidok>
          {/* ŻÓŁTY PASEK */}
          <RegionWidok r={reg('MESSAGE_BAR')} szer={SZER} wys={PASEK} rozwiaz={rozwiaz} sx={{ bgcolor: (!komunikatPaska && pasek?.tlo) || '#FFD400' }}>
            <Typography data-testid="podglad-pasek" sx={{ position: 'relative', zIndex: 1, lineHeight: `${PASEK}px`, px: 1.5, fontSize: 12,
              fontWeight: pasek?.wyroznienie === 'BOLD' ? 800 : 600, textTransform: pasek?.wyroznienie === 'UPPERCASE' ? 'uppercase' : 'none',
              color: (!komunikatPaska && pasek?.kolorTekstu) || '#1d1b1a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {tekstPaska} <span style={{ float: 'right' }}>🛒 Koszyk</span>
            </Typography>
          </RegionWidok>
          {/* TREŚĆ */}
          <Box sx={{ position: 'relative', height: WYS_EKRANU - NAGLOWEK - PASEK - NAWIGACJA, overflow: 'hidden' }}>
            <Box sx={{ position: 'absolute', inset: 0 }}>
              <RegionWidok r={reg('CONTENT_BACKGROUND')} szer={SZER} wys={WYS_EKRANU - NAGLOWEK - PASEK - NAWIGACJA} rozwiaz={rozwiaz} />
            </Box>
            <Box sx={{ position: 'relative' }}>
              {wysHero ? <RegionWidok r={reg('EVENT_HERO')} szer={SZER} wys={wysHero} rozwiaz={rozwiaz} /> : null}
              {wysPre ? <RegionWidok r={reg('PRE_RESTAURANTS')} szer={SZER} wys={wysPre} rozwiaz={rozwiaz} /> : null}
              {sekcja('Restauracje')}
              {sekcja('Polecane dania')}
              {wysPost ? <RegionWidok r={reg('POST_CONTENT')} szer={SZER} wys={wysPost} rozwiaz={rozwiaz} /> : null}
              <RegionWidok r={reg('FOOTER')} szer={SZER} wys={110} rozwiaz={rozwiaz} sx={{ mt: 1.5 }}>
                <Typography sx={{ position: 'relative', zIndex: 1, p: 1.5, fontSize: 12, fontWeight: 700, color: paleta?.tekst ?? '#1d1b1a' }}>Social media</Typography>
              </RegionWidok>
            </Box>
            <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
              <RegionWidok r={reg('CONTENT_FOREGROUND')} szer={SZER} wys={WYS_EKRANU - NAGLOWEK - PASEK - NAWIGACJA} rozwiaz={rozwiaz} sx={{ bgcolor: 'transparent' }} />
            </Box>
          </Box>
          {/* DOLNA NAWIGACJA */}
          <RegionWidok r={reg('BOTTOM_NAV')} szer={SZER} wys={NAWIGACJA} rozwiaz={rozwiaz} sx={{ bgcolor: '#fff', borderTop: '1px solid #e5e2da' }}>
            <Stack direction="row" sx={{ justifyContent: 'space-around', alignItems: 'center', height: NAWIGACJA, position: 'relative', zIndex: 1, fontSize: 10 }}>
              <span>Menu</span><span>Zamówienia</span><span>Portfel</span><span>Profil</span>
            </Stack>
          </RegionWidok>
          {/* CAŁY EKRAN */}
          <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
            <RegionWidok r={reg('FULL_SCREEN_OVERLAY')} szer={SZER} wys={WYS_EKRANU} rozwiaz={rozwiaz} sx={{ bgcolor: 'transparent' }} />
          </Box>
        </Box>
      </Box>
      {motyw ? (
        <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', justifyContent: 'center', gap: 0.5 }}>
          {efekty.length ? <Chip size="small" label={`Animacje: ${efekty.join(', ')}`} /> : <Chip size="small" label="Bez animacji" />}
          {paleta ? <Chip size="small" label="Własna paleta menu" /> : null}
          {pasek ? <Chip size="small" label={`Pasek okazji: „${pasek.tekst}"`} /> : null}
        </Stack>
      ) : (
        <Typography variant="body2" role="status" data-testid="podglad-zwykly-wyglad" sx={{ color: 'text.secondary', textAlign: 'center' }}>
          Silnik aplikacji odrzuca tę paczkę — aplikacja pokaże zwykły wygląd.
        </Typography>
      )}
      <Typography variant="caption" sx={{ color: 'text.secondary', textAlign: 'center', maxWidth: SZER }}>
        Scena z prawdziwej paczki (ten sam walidator co aplikacja). Makieta ekranu i ruch są przybliżone.
      </Typography>
    </Stack>
  );
}
